'use client';

import { ReactNode, useEffect, useCallback, useState, createContext, useContext } from 'react';
import { useAuth, useUser } from '@clerk/nextjs';
import { usePathname } from 'next/navigation';
import { MotionConfig } from 'motion/react';
import { List } from '@phosphor-icons/react';
import { Sidebar } from '@/components/sidebar';
import { GlobalSearch } from '@/components/global-search';
import { GalexiaLogo } from '@/components/galexia-logo';
import { OnboardingModal } from '@/components/onboarding-modal';
import { SessionProvider, useSession } from '@/contexts/session-context';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import posthog from 'posthog-js';

// Context for mobile sidebar state + desktop sidebar collapse control.
// Exposed so artifact-heavy surfaces (GenUIChat with an open dashboard /
// visualization panel) can imperatively collapse the chat-history nav
// to free up horizontal space. Users can still expand it manually.
interface MobileSidebarContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  desktopCollapsed: boolean;
  setDesktopCollapsed: (collapsed: boolean) => void;
}

const MobileSidebarContext = createContext<MobileSidebarContextType>({
  isOpen: false,
  setIsOpen: () => {},
  desktopCollapsed: true,
  setDesktopCollapsed: () => {},
});

export const useMobileSidebar = () => useContext(MobileSidebarContext);

function LayoutContent({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  // startsWith (not ===) so Clerk's own path-routed sub-steps under
  // /sign-in/* and /sign-up/* (SSO callback, factor-one, etc. — see
  // routes/sign-in.tsx's splat route) stay chromeless too.
  const isAuthPage = pathname.startsWith('/sign-in') || pathname.startsWith('/sign-up');
  const isChromelessPage = isAuthPage || pathname.startsWith('/v/') || pathname.startsWith('/s/') || pathname.startsWith('/d/') || pathname.startsWith('/x/') || pathname.startsWith('/screenshot/');

  // Share/screenshot pages get a clean layout without sidebar or mobile header
  if (isChromelessPage) {
    return <div className="h-screen overflow-hidden">{children}</div>;
  }

  return <AppLayout>{children}</AppLayout>;
}

function AppLayout({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(true);
  const {
    currentSessionId,
    setCurrentSessionId,
    sessions,
    setSessions,
    refreshSessions,
    projects,
    refreshProjects,
    createProject,
    renameProject,
    deleteProject,
    assignSessionToProject,
  } = useSession();
  // Projects feature: null = "All" (no filter). Reset whenever a chat outside
  // the active project is opened directly (e.g. via a shared link) so the
  // sidebar doesn't hide the session the user is looking at.
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);

  // Initialize plan from localStorage for instant display (no flicker)
  const [userPlan, setUserPlan] = useState<'free' | 'pro'>(() => {
    if (typeof window !== 'undefined') {
      const cached = localStorage.getItem('galexia_plan');
      if (cached === 'pro') return 'pro';
    }
    return 'free';
  });
  const [credits, setCredits] = useState(5);
  const isAdmin = user?.publicMetadata?.role === 'admin';

  // Update from Clerk publicMetadata (loads fast, before API call)
  useEffect(() => {
    if (user?.publicMetadata?.plan) {
      const plan = user.publicMetadata.plan === 'pro' ? 'pro' : 'free';
      setUserPlan(plan);
      localStorage.setItem('galexia_plan', plan);
    }
  }, [user]);

  // PostHog: identify user
  useEffect(() => {
    if (isSignedIn && user) {
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName,
        plan: (user.publicMetadata?.plan as string) || 'free',
      });
    }
  }, [isSignedIn, user]);

  // Fetch sessions immediately, but defer billing summary. The portal route
  // imports Stripe + usage/db code, which is expensive during cold dev
  // compilation and should not compete with first paint or chat submit.
  useEffect(() => {
    if (!isSignedIn) return;

    refreshSessions();
    refreshProjects();

    const syncBillingSummary = () => {
      fetch('/api/stripe/portal')
        .then(res => res.json())
        .then(data => {
          if (data && !data.error) {
            const plan = data.plan === 'pro' || data.plan === 'team' ? 'pro' : 'free';
            setUserPlan(plan);
            setCredits(data.credits);
            localStorage.setItem('galexia_plan', plan);
          }
        })
        .catch(err => console.error('Failed to fetch user status:', err));
    };

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(syncBillingSummary, { timeout: 8000 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = setTimeout(syncBillingSummary, 3000);
    return () => clearTimeout(timeoutId);
  }, [isSignedIn, refreshSessions, refreshProjects]);

  const handleNewChat = () => {
    setCurrentSessionId(null);
    setSidebarOpen(false);
  };

  const handleSessionSelect = (sessionId: string | null) => {
    setCurrentSessionId(sessionId);
    setSidebarOpen(false);
  };

  const handleDeleteSession = useCallback(async (sessionId: string) => {
    try {
      const res = await fetch(`/api/sessions/${sessionId}`, { method: 'DELETE' });
      if (res.ok) {
        setSessions(sessions.filter(s => s.id !== sessionId));
        if (currentSessionId === sessionId) {
          setCurrentSessionId(null);
        }
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  }, [sessions, currentSessionId, setSessions, setCurrentSessionId]);

  const handleRenameSession = useCallback(async (sessionId: string, title: string) => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      return;
    }

    try {
      const res = await fetch(`/api/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: trimmedTitle }),
      });

      if (!res.ok) {
        throw new Error('Failed to rename chat');
      }

      setSessions(sessions.map((session) => (
        session.id === sessionId ? { ...session, title: trimmedTitle } : session
      )));
    } catch (error) {
      console.error('Failed to rename session:', error);
    }
  }, [sessions, setSessions]);

  // Projects feature — filter the sidebar's session list to the active
  // project ("All" == activeProjectId null resets to the full list).
  const visibleSessions = activeProjectId
    ? sessions.filter((session) => session.projectId === activeProjectId)
    : sessions;

  return (
    <MobileSidebarContext.Provider value={{
      isOpen: sidebarOpen,
      setIsOpen: setSidebarOpen,
      desktopCollapsed,
      setDesktopCollapsed,
    }}>
      <div className="flex h-screen">
        {/* Desktop Sidebar - hidden on mobile */}
        <div className="hidden md:block">
          <Sidebar
            currentSessionId={currentSessionId}
            onSessionSelect={setCurrentSessionId}
            onNewChat={handleNewChat}
            sessions={visibleSessions}
            onDeleteSession={handleDeleteSession}
            onRenameSession={handleRenameSession}
            userPlan={userPlan}
            creditsRemaining={credits}
            isAdmin={isAdmin}
            collapsed={desktopCollapsed}
            onCollapsedChange={setDesktopCollapsed}
            projects={projects}
            activeProjectId={activeProjectId}
            onProjectSelect={setActiveProjectId}
            onCreateProject={createProject}
            onRenameProject={renameProject}
            onDeleteProject={deleteProject}
            onAssignSessionProject={assignSessionToProject}
          />
        </div>

        {/* Mobile Sidebar - Sheet drawer */}
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="p-0 w-[280px]" showCloseButton={false}>
            <Sidebar
              currentSessionId={currentSessionId}
              onSessionSelect={handleSessionSelect}
              onNewChat={handleNewChat}
              sessions={visibleSessions}
              onDeleteSession={handleDeleteSession}
              onRenameSession={handleRenameSession}
              userPlan={userPlan}
              creditsRemaining={credits}
              isAdmin={isAdmin}
              className="w-full border-r-0"
              projects={projects}
              activeProjectId={activeProjectId}
              onProjectSelect={setActiveProjectId}
              onCreateProject={createProject}
              onRenameProject={renameProject}
              onDeleteProject={deleteProject}
              onAssignSessionProject={assignSessionToProject}
            />
          </SheetContent>
        </Sheet>

        <main className="flex-1 overflow-hidden flex flex-col">
          {/* Mobile Header with menu button */}
          <header className="md:hidden flex items-center gap-3 px-4 py-3 border-b bg-background/95 backdrop-blur-sm sticky top-0 z-40">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              className="shrink-0"
            >
              <List weight="bold" className="size-5" />
              <span className="sr-only">Open menu</span>
            </Button>
            <GalexiaLogo size={28} />
            <span className="font-semibold text-lg tracking-wide">Galexia</span>
          </header>
          
          <div className={`flex-1 ${
            pathname === '/' || pathname.startsWith('/chat/') || pathname.startsWith('/canvas') || pathname.startsWith('/admin/chats/')
              ? 'overflow-hidden'
              : 'overflow-y-auto'
          }`}>
            {children}
          </div>
        </main>
        <OnboardingModal />
        <GlobalSearch onSelectProject={setActiveProjectId} />
      </div>
    </MobileSidebarContext.Provider>
  );
}

export function ClientLayout({ children }: { children: ReactNode }) {
  return (
    // reducedMotion="user" makes every motion.* component across the app
    // honour the OS prefers-reduced-motion preference globally — covers
    // both motion/react and framer-motion imports (same package).
    <MotionConfig reducedMotion="user">
      <SessionProvider>
        <LayoutContent>{children}</LayoutContent>
      </SessionProvider>
    </MotionConfig>
  );
}
